import type { SurveyAnswersState } from "@utils/redux/slices/surveyAnswers";
import { resetCurrentAnswers } from "src/utils/redux/slices/currentAnswers";
import { saveSurveyAnswers } from "src/utils/redux/slices/userData";
import type { ActionHandlerParams } from "..";
import { getInternalSurveyNamespace } from "src/utils/survey/surveyNamespace";
import { getPageSetSurvey } from "./util";

type Params = {
  keys: string[];
};

function omitKeys(
  source: SurveyAnswersState,
  keys: readonly string[]
): SurveyAnswersState {
  const next = { ...source };
  for (const key of keys) {
    delete next[key];
  }
  return next;
}

/**
 * Drops the given question keys from the active survey namespace in
 * `currentAnswers` and from persisted `userData` (e.g. stale answers from a
 * prior visit). Intended for `onSkip` when a conditional page is not shown.
 */
export async function removeSurveyAnswerKeys({
  params,
  store,
  pageSet,
  page,
}: ActionHandlerParams) {
  const { keys } = params as Params;
  if (!keys?.length) return;

  const { surveyNameSpace } = getPageSetSurvey(pageSet, page);
  const internalNamespace = getInternalSurveyNamespace(surveyNameSpace);
  const state = store.getState();

  const current = state.currentAnswers.questionAnswers[surveyNameSpace] || {};
  const persisted =
    (state.userData[internalNamespace] as SurveyAnswersState | undefined) ?? {};

  const hadInCurrent = keys.some((k) => k in current);
  const hadInPersisted = keys.some((k) => k in persisted);

  if (!hadInCurrent && !hadInPersisted) return;

  if (hadInCurrent) {
    store.dispatch(
      resetCurrentAnswers({
        namespace: surveyNameSpace,
        answers: omitKeys(current, keys),
      })
    );
  }

  if (hadInPersisted) {
    await store.dispatch(
      saveSurveyAnswers(omitKeys(persisted, keys), {
        surveyNameSpace: internalNamespace,
        overwrite: true,
      })
    );
  }
}
